import { Reveal } from "../lib/motion";
import { Layers, RefreshCcw, ShieldQuestion } from "lucide-react";

const POSTER =
  "https://customer-assets-lqy194kg.emergentagent.net/job_nexis-future/artifacts/51378d9273cdc551_image.png";

const POINTS = [
  {
    icon: Layers,
    title: "A true representation of reality",
    desc: "Intelligence needs more than data. People, assets, processes, environments, and relationships — held as one continuous, trusted governed state.",
  },
  {
    icon: RefreshCcw,
    title: "Reality changes. State must evolve with it.",
    desc: "Evidence changes. Context changes. Risk changes. Authority changes. Knowledge evolves — and the state a system thinks against evolves with it.",
  },
  {
    icon: ShieldQuestion,
    title: "Can I? May I? May I still?",
    desc: "Capability, permissibility, continuous validation. Authority is governed, auditable, and re-checked at the moment of action — state precedes computation.",
  },
];

export default function GovernedState() {
  return (
    <section
      id="governed-state"
      className="relative border-t border-white/5 bg-nexis-bg/85 py-28 backdrop-blur-sm md:py-40"
      data-testid="governed-state-section"
    >
      <div className="orb orb-blue right-[8%] top-[15%] h-[380px] w-[380px]" />
      <div className="mx-auto grid max-w-7xl items-center gap-16 px-6 md:px-12 lg:grid-cols-[1fr_1.05fr]">
        <Reveal delay={0.15}>
          <a
            href={POSTER}
            target="_blank"
            rel="noreferrer"
            className="group block overflow-hidden rounded-[2rem] glass ring-1 ring-white/10"
            data-testid="governed-state-poster"
          >
            <img
              src={POSTER}
              alt="Before a system can think, what does it think against? — the NEXIS governed state architecture"
              loading="lazy"
              className="w-full object-contain transition-transform duration-700 group-hover:scale-[1.015]"
            />
          </a>
        </Reveal>

        <div>
          <Reveal>
            <span className="font-mono text-xs tracking-[0.25em] text-nexis-blue">— SCENE 05.5 / GOVERNED STATE</span>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 font-display text-4xl font-light leading-tight tracking-tight lg:text-6xl" data-testid="governed-state-headline">
              Before a system can think, <span className="text-gradient">what does it think against?</span>
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 max-w-lg text-base leading-relaxed text-white/60">
              Intelligence tells you what a system can do. Governed state defines what it must operate
              against — and what governs its authority. From intelligence, to governed state, to
              authorized execution.
            </p>
          </Reveal>

          <div className="mt-12 space-y-4">
            {POINTS.map((p, i) => (
              <Reveal key={p.title} delay={0.1 + i * 0.08}>
                <div className="glass glass-hover flex gap-5 rounded-2xl p-6" data-testid={`governed-state-point-${i}`}>
                  <p.icon className="mt-0.5 h-5 w-5 shrink-0 text-nexis-blue" />
                  <div>
                    <h3 className="font-display text-base font-medium">{p.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-white/50">{p.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
